import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Briefcase, AlertCircle, FileText, DollarSign, Clock, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { formatApiErrorDetail } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ExpertRegister() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [form, setForm] = useState({
    fullName: '', email: '', password: '', confirmPassword: '',
    headline: '', bio: '', skills: '', yearsExperience: '', certifications: '', linkedin: ''
  });
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (!resume) return setError('Please upload your resume (PDF, DOC or DOCX)');
    if (resume.size > 5 * 1024 * 1024) return setError('Resume must be under 5MB');

    const fd = new FormData();
    ['fullName', 'email', 'password', 'headline', 'bio', 'skills', 'yearsExperience', 'certifications', 'linkedin']
      .forEach((k) => fd.append(k, form[k]));
    fd.append('resume', resume);

    setLoading(true);
    try {
      await api.post('/expert/register', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
      navigate('/expert/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0078D4] p-3 rounded-full">
              <Briefcase className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-[#003366] mb-3">Become a Solving365 Expert</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Are you a Microsoft 365 or Azure AD specialist? Join our expert network, resolve customer
            queries and earn up to 50% of every query you resolve.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <Card className="border-t-4 border-t-[#0078D4]">
            <CardContent className="pt-6 text-center">
              <DollarSign className="h-8 w-8 text-[#0078D4] mx-auto mb-2" />
              <p className="font-semibold text-[#003366]">Earn up to 50%</p>
              <p className="text-sm text-gray-600">Get 50% of the query fee for 5-star resolutions ($50 on standard, $100 on priority)</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-[#0078D4]">
            <CardContent className="pt-6 text-center">
              <Clock className="h-8 w-8 text-[#0078D4] mx-auto mb-2" />
              <p className="font-semibold text-[#003366]">Work Anytime</p>
              <p className="text-sm text-gray-600">Grab queries from the pool whenever you're available</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-[#0078D4]">
            <CardContent className="pt-6 text-center">
              <Award className="h-8 w-8 text-[#0078D4] mx-auto mb-2" />
              <p className="font-semibold text-[#003366]">Build Reputation</p>
              <p className="text-sm text-gray-600">Customer star ratings showcase your expertise</p>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-lg max-w-3xl mx-auto">
          <CardHeader>
            <h2 className="text-xl font-bold text-[#003366]">Expert Application</h2>
            <p className="text-sm text-gray-600">Your profile will be reviewed by Solving365 before you can start grabbing queries.</p>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6" data-testid="expert-register-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-700 font-medium">Full Name <span className="text-red-500">*</span></Label>
                  <Input name="fullName" value={form.fullName} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-fullname-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Email <span className="text-red-500">*</span></Label>
                  <Input name="email" type="email" value={form.email} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-email-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Password <span className="text-red-500">*</span></Label>
                  <Input name="password" type="password" value={form.password} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-password-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Confirm Password <span className="text-red-500">*</span></Label>
                  <Input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-confirm-password-input" />
                </div>
              </div>

              <div>
                <Label className="text-gray-700 font-medium">Professional Headline <span className="text-red-500">*</span></Label>
                <Input name="headline" placeholder="e.g. Senior Microsoft 365 Consultant | Exchange & Azure AD" value={form.headline} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-headline-input" />
              </div>

              <div>
                <Label className="text-gray-700 font-medium">About You / Showcase <span className="text-red-500">*</span></Label>
                <Textarea name="bio" placeholder="Describe your experience, notable projects, environments you've managed..." value={form.bio} onChange={handleChange} required className="mt-1 min-h-[120px]" data-testid="expert-bio-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-700 font-medium">Skills (comma separated) <span className="text-red-500">*</span></Label>
                  <Input name="skills" placeholder="Exchange Online, Azure AD, PowerShell, Teams" value={form.skills} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-skills-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Years of Experience <span className="text-red-500">*</span></Label>
                  <Input name="yearsExperience" type="number" min="0" max="50" value={form.yearsExperience} onChange={handleChange} required className="mt-1 h-11" data-testid="expert-experience-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Certifications</Label>
                  <Input name="certifications" placeholder="e.g. MS-102, AZ-104, SC-300" value={form.certifications} onChange={handleChange} className="mt-1 h-11" data-testid="expert-certs-input" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">LinkedIn Profile</Label>
                  <Input name="linkedin" placeholder="https://linkedin.com/in/..." value={form.linkedin} onChange={handleChange} className="mt-1 h-11" data-testid="expert-linkedin-input" />
                </div>
              </div>

              <div>
                <Label className="text-gray-700 font-medium">Resume (PDF, DOC, DOCX — max 5MB) <span className="text-red-500">*</span></Label>
                <div className="mt-1 flex items-center space-x-3">
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => { setResume(e.target.files[0] || null); setError(''); }}
                    className="h-11 pt-2"
                    data-testid="expert-resume-input"
                  />
                </div>
                {resume && (
                  <p className="text-sm text-green-700 mt-2 flex items-center" data-testid="expert-resume-selected">
                    <FileText className="h-4 w-4 mr-1" /> {resume.name} ({(resume.size / 1024).toFixed(0)} KB)
                  </p>
                )}
              </div>

              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#0078D4] hover:bg-[#005a9e] font-semibold text-lg" data-testid="expert-register-submit">
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            </form>
            <p className="text-sm text-gray-600 text-center mt-4">
              Already an expert? <Link to="/login" className="text-[#0078D4] hover:underline font-semibold">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
